import { useEffect, useRef, useState } from "react";
import { http, API } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
    DownloadSimple,
    UploadSimple,
    FileXls,
    Database,
    Warning,
    CheckCircle,
    ArrowsClockwise,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const CSV_TABLES = [
    { v: "users", l: "Usuarios" },
    { v: "customers", l: "Clientes" },
    { v: "projects", l: "Proyectos" },
    { v: "assignments", l: "Asignaciones" },
    { v: "timesheet_entries", l: "Entradas de horas" },
    { v: "absences", l: "Vacaciones y ausencias" },
    { v: "expenses", l: "Gastos" },
];

export default function AdminData() {
    const [summary, setSummary] = useState(null);
    const [csvTable, setCsvTable] = useState("users");
    const [wipe, setWipe] = useState(true);
    const [importResult, setImportResult] = useState(null);
    const [importing, setImporting] = useState(false);
    const [csvImporting, setCsvImporting] = useState(false);
    const [csvResult, setCsvResult] = useState(null);
    const xmlRef = useRef();
    const csvRef = useRef();

    const loadSummary = () => {
        http.get("/admin/data-summary")
            .then((r) => setSummary(r.data))
            .catch(() => null);
    };

    useEffect(() => {
        loadSummary();
    }, []);

    const download = (url, filename) => {
        // Use fetch with credentials to keep cookies then trigger blob download
        fetch(url, { credentials: "include" })
            .then((r) => {
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                return r.blob();
            })
            .then((blob) => {
                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                link.remove();
            })
            .catch((err) => toast.error("Error de descarga: " + err.message));
    };

    const importXml = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!window.confirm(
            wipe
                ? "Vas a IMPORTAR y REEMPLAZAR clientes, proyectos, asignaciones y entradas de horas. Los usuarios ya existentes se conservan. ¿Continuar?"
                : "Vas a AÑADIR datos sin borrar los existentes. ¿Continuar?"
        )) {
            e.target.value = "";
            return;
        }
        setImporting(true);
        setImportResult(null);
        try {
            // Auto-compress plain XML with gzip in-browser to bypass proxy body-size limits.
            // Files already gzipped (.gz / .xml.gz) are sent as-is.
            let uploadBlob = file;
            let uploadName = file.name;
            const looksGzipped =
                file.name.toLowerCase().endsWith(".gz") ||
                file.type === "application/gzip" ||
                file.type === "application/x-gzip";
            if (!looksGzipped && typeof CompressionStream !== "undefined") {
                const gz = file.stream().pipeThrough(new CompressionStream("gzip"));
                uploadBlob = await new Response(gz).blob();
                uploadName = file.name + ".gz";
            }
            const fd = new FormData();
            fd.append("file", uploadBlob, uploadName);
            const r = await fetch(`${API}/admin/import/ehour?wipe=${wipe}`, {
                method: "POST",
                credentials: "include",
                body: fd,
            });
            let data;
            try {
                data = await r.json();
            } catch {
                data = { detail: `HTTP ${r.status} ${r.statusText}` };
            }
            if (!r.ok) throw new Error(data.detail || r.statusText);
            setImportResult(data);
            toast.success("Importación completada");
            loadSummary();
        } catch (err) {
            toast.error("Error: " + err.message);
        } finally {
            setImporting(false);
            e.target.value = "";
        }
    };

    const importCsv = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setCsvImporting(true);
        setCsvResult(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await fetch(`${API}/admin/import/csv/${csvTable}`, {
                method: "POST",
                credentials: "include",
                body: fd,
            });
            const data = await r.json();
            if (!r.ok) throw new Error(data.detail || r.statusText);
            setCsvResult({ table: csvTable, ...data });
            toast.success(
                `${data.inserted} insertados · ${data.updated} actualizados`,
            );
            loadSummary();
        } catch (err) {
            toast.error("Error: " + err.message);
        } finally {
            setCsvImporting(false);
            e.target.value = "";
        }
    };

    const fmt = (n) => (Number(n) || 0).toLocaleString("es-ES");

    return (
        <div data-testid="page-admin-data">
            <PageHeader
                eyebrow="Administración"
                title="Datos"
                description="Copia de seguridad y migración de datos. Import/export en formato eHour XML y CSV por tabla."
                actions={
                    <Button
                        variant="outline"
                        className="rounded-sm"
                        onClick={loadSummary}
                        data-testid="refresh-summary"
                    >
                        <ArrowsClockwise size={14} className="mr-2" />
                        Actualizar
                    </Button>
                }
            />
            <div className="p-6 lg:p-10 space-y-6">
                {/* Data summary */}
                <div className="border border-slate-200 bg-white">
                    <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-3">
                        <Database size={20} className="text-slate-500" />
                        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                            Estado actual
                        </div>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-8 gap-px bg-slate-200">
                        {summary &&
                            Object.entries(summary).map(([k, v]) => (
                                <div key={k} className="bg-white p-4">
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 truncate">
                                        {k.replace(/_/g, " ")}
                                    </div>
                                    <div className="font-mono text-xl font-bold mt-1">
                                        {fmt(v)}
                                    </div>
                                </div>
                            ))}
                    </div>
                </div>

                {/* eHour XML */}
                <div className="border border-slate-200 bg-white">
                    <div className="px-6 py-5 border-b border-slate-200">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                            Backup completo
                        </div>
                        <div className="font-display text-xl font-semibold mt-1">
                            Formato eHour XML
                        </div>
                        <p className="text-sm text-slate-600 mt-1">
                            Compatible con el backup XML original de eHour 1.4.x.
                            Contiene usuarios, clientes, proyectos, asignaciones y todas
                            las entradas de horas.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-slate-200">
                        <div className="bg-white p-6">
                            <div className="flex items-center gap-2 mb-3">
                                <DownloadSimple size={18} className="text-[#002BAF]" />
                                <div className="font-display text-lg font-semibold">
                                    Exportar
                                </div>
                            </div>
                            <p className="text-sm text-slate-600 mb-4">
                                Descarga un backup XML con todos los datos actuales del
                                sistema.
                            </p>
                            <Button
                                onClick={() =>
                                    download(
                                        `${API}/admin/export/ehour.xml`,
                                        `ingartek-ehour-${new Date().toISOString().slice(0, 10)}.xml`,
                                    )
                                }
                                className="rounded-sm bg-[#002BAF] hover:bg-[#001F82]"
                                data-testid="export-ehour-btn"
                            >
                                <DownloadSimple size={14} className="mr-2" />
                                Descargar backup XML
                            </Button>
                        </div>
                        <div className="bg-white p-6">
                            <div className="flex items-center gap-2 mb-3">
                                <UploadSimple size={18} className="text-[#002BAF]" />
                                <div className="font-display text-lg font-semibold">
                                    Importar
                                </div>
                            </div>
                            <p className="text-sm text-slate-600 mb-3">
                                Sube un XML eHour para cargarlo en el sistema.
                                Se comprime automáticamente en tu navegador antes de
                                enviarlo (esquiva límites del proxy en producción).
                            </p>
                            <div className="flex items-center gap-2 mb-4">
                                <Checkbox
                                    id="wipe"
                                    checked={wipe}
                                    onCheckedChange={(v) => setWipe(!!v)}
                                    data-testid="import-wipe"
                                />
                                <label
                                    htmlFor="wipe"
                                    className="text-xs cursor-pointer text-slate-700"
                                >
                                    Vaciar clientes, proyectos, asignaciones y entradas
                                    antes de importar (recomendado)
                                </label>
                            </div>
                            <input
                                ref={xmlRef}
                                type="file"
                                accept=".xml,.gz,.xml.gz,text/xml,application/gzip"
                                onChange={importXml}
                                className="hidden"
                                data-testid="import-ehour-file"
                            />
                            <Button
                                onClick={() => xmlRef.current?.click()}
                                disabled={importing}
                                variant="outline"
                                className="rounded-sm"
                                data-testid="import-ehour-btn"
                            >
                                <UploadSimple size={14} className="mr-2" />
                                {importing ? "Importando…" : "Elegir archivo XML"}
                            </Button>
                            {importing && (
                                <div className="mt-3">
                                    <div className="tracing-beam h-1 bg-slate-100" />
                                    <div className="text-xs text-slate-500 mt-1">
                                        Puede tardar varios minutos si el archivo es
                                        grande (20MB+).
                                    </div>
                                </div>
                            )}
                            {importResult && (
                                <div className="mt-4 border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900">
                                    <div className="flex items-center gap-2 font-semibold mb-1">
                                        <CheckCircle size={14} /> Importación
                                        completada
                                    </div>
                                    <div className="font-mono">
                                        {Object.entries(importResult).map(([k, v]) => (
                                            <div key={k}>
                                                {k}: {fmt(v)}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* CSV per table */}
                <div className="border border-slate-200 bg-white">
                    <div className="px-6 py-5 border-b border-slate-200">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                            Por tabla
                        </div>
                        <div className="font-display text-xl font-semibold mt-1">
                            CSV
                        </div>
                        <p className="text-sm text-slate-600 mt-1">
                            Exporta o importa una única tabla en formato CSV (separador{" "}
                            <code className="font-mono">;</code>, UTF-8). Si una fila
                            trae el ID existente se actualiza; si no, se crea una nueva.
                        </p>
                    </div>
                    <div className="p-6 flex flex-wrap items-end gap-3">
                        <div className="min-w-[260px]">
                            <label className="text-xs text-slate-600">Tabla</label>
                            <Select value={csvTable} onValueChange={setCsvTable}>
                                <SelectTrigger
                                    className="mt-1 rounded-sm border-slate-300"
                                    data-testid="csv-table-select"
                                >
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {CSV_TABLES.map((t) => (
                                        <SelectItem key={t.v} value={t.v}>
                                            {t.l}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <Button
                            onClick={() =>
                                download(
                                    `${API}/admin/export/csv/${csvTable}`,
                                    `${csvTable}-${new Date().toISOString().slice(0, 10)}.csv`,
                                )
                            }
                            className="rounded-sm bg-[#002BAF] hover:bg-[#001F82]"
                            data-testid="csv-export-btn"
                        >
                            <FileXls size={14} className="mr-2" />
                            Exportar CSV
                        </Button>
                        <input
                            ref={csvRef}
                            type="file"
                            accept=".csv,text/csv"
                            onChange={importCsv}
                            className="hidden"
                            data-testid="csv-import-file"
                        />
                        <Button
                            variant="outline"
                            onClick={() => csvRef.current?.click()}
                            disabled={csvImporting}
                            className="rounded-sm"
                            data-testid="csv-import-btn"
                        >
                            <UploadSimple size={14} className="mr-2" />
                            {csvImporting ? "Importando…" : "Importar CSV"}
                        </Button>
                    </div>
                    {csvResult && (
                        <div className="mx-6 mb-6 border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900">
                            <div className="flex items-center gap-2 font-semibold mb-1">
                                <CheckCircle size={14} /> Import CSV ({csvResult.table})
                            </div>
                            <div className="font-mono">
                                insertados: {fmt(csvResult.inserted)} · actualizados:{" "}
                                {fmt(csvResult.updated)}
                            </div>
                            {csvResult.errors?.length > 0 && (
                                <div className="mt-2">
                                    <div className="flex items-center gap-1 text-amber-800 font-semibold">
                                        <Warning size={12} /> Errores (
                                        {csvResult.errors.length})
                                    </div>
                                    <ul className="mt-1 list-disc list-inside">
                                        {csvResult.errors.slice(0, 5).map((e, i) => (
                                            <li key={i}>{e}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
